import { ArrowLeft, MessageCircle, Pencil, Plus, Trash2 } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useI18n } from "@/context/i18n";

import { WhatsappMetaConfigFormDialog } from "./components";
import { useWhatsappMetaConfig } from "./whatsapp-meta-config.hooks";

export const WhatsappMetaConfigPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();

  // Quando acessado via /clients/:clientId/whatsapp-meta, o hook usa o clientId como escopo
  const { clientId } = useParams<{ clientId: string }>();
  const hook = useWhatsappMetaConfig(clientId);

  const detailPath = (configId: string) =>
    clientId
      ? `/clients/${clientId}/whatsapp-meta/${configId}`
      : `/whatsapp-meta/${configId}`;

  const handleBack = () => {
    if (clientId) {
      navigate("/clients", { state: { openClientId: clientId } });
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="flex min-h-full w-full flex-1 flex-col bg-background">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 md:px-6 md:py-8">
        {clientId && (
          <Button
            type="button"
            variant="ghost"
            className="w-fit gap-2 px-0 text-muted-foreground hover:text-foreground"
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4" />
            {t("generic.goBack")}
          </Button>
        )}

        <header className="flex flex-col gap-4 border-b border-border/60 pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <MessageCircle className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 space-y-1">
              <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">
                {t("campaigns.whatsappMeta.title")}
              </h1>
              <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {t("campaigns.whatsappMeta.description")}
              </p>
            </div>
          </div>
          <Button
            type="button"
            className="w-full shrink-0 sm:w-auto"
            onClick={hook.openCreate}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("campaigns.whatsappMeta.add")}
          </Button>
        </header>

        <div
          className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm leading-relaxed text-muted-foreground"
          role="note"
        >
          {t("campaigns.whatsappMeta.workflowNote")}
        </div>

        <Card className="border-border/80 shadow-sm">
          <CardHeader className="border-b border-border/60 pb-4">
            <CardTitle className="text-lg font-semibold">
              {t("campaigns.whatsappMeta.connectionsTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {hook.isLoading ? (
              <p className="text-muted-foreground text-sm">{t("generic.loading")}</p>
            ) : hook.isConfigsError ? (
              <div
                className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                role="alert"
              >
                <p className="font-medium">{t("campaigns.whatsappMeta.errors.loadConfigs")}</p>
                {hook.configsErrorMessage ? (
                  <p className="mt-1 text-destructive/90">{hook.configsErrorMessage}</p>
                ) : null}
              </div>
            ) : !hook.configs.length ? (
              <p className="text-muted-foreground text-sm">
                {t("campaigns.whatsappMeta.empty")}
              </p>
            ) : (
              <div className="-mx-1 overflow-x-auto px-1">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("campaigns.whatsappMeta.table.label")}</TableHead>
                      <TableHead>{t("campaigns.whatsappMeta.table.wabaId")}</TableHead>
                      <TableHead>{t("campaigns.whatsappMeta.table.token")}</TableHead>
                      <TableHead className="text-right">
                        {t("campaigns.whatsappMeta.table.actions")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hook.configs.map((row) => (
                      <TableRow
                        key={row.id}
                        className="cursor-pointer"
                        onClick={() => navigate(detailPath(row.id))}
                      >
                        <TableCell>{row.label || "—"}</TableCell>
                        <TableCell className="font-mono text-xs max-w-[180px] truncate">
                          {row.waba_id}
                        </TableCell>
                        <TableCell className="text-sm">
                          {row.has_access_token
                            ? t("campaigns.whatsappMeta.tokenConfigured")
                            : t("campaigns.whatsappMeta.tokenMissing")}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              hook.openEdit(row);
                            }}
                            aria-label={t("campaigns.whatsappMeta.edit")}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              hook.setDeleteTargetId(row.id);
                            }}
                            aria-label={t("campaigns.whatsappMeta.delete")}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <WhatsappMetaConfigFormDialog hook={hook} />

        <AlertDialog
          open={!!hook.deleteTargetId}
          onOpenChange={(open) => !open && hook.setDeleteTargetId(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("campaigns.whatsappMeta.deleteConfirmTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("campaigns.whatsappMeta.deleteConfirmDescription")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("campaigns.whatsappMeta.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                disabled={hook.isDeleting}
                onClick={() => {
                  if (hook.deleteTargetId) {
                    hook.onDelete(hook.deleteTargetId);
                  }
                }}
              >
                {t("campaigns.whatsappMeta.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
